import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import withLifecycle from '@hocs/with-lifecycle';

import { IconError, IconClock, IconDone, IconAgent, SpacerVertical } from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import { components as clientComponents } from 'mage-client';
import { MOET_CONSTANTS, LoaderIcon, Alert, withPolling } from 'moet-core';

import {
  getMerchantStatus,
  getUserMerchantId,
  isAwaitingPayoutsRequestAfterFirstTransaction,
  isManagedMerchant,
  merchantRegion,
} from '@/selectors/merchant';
import { isFetchingMerchant } from '@/selectors/loadingStates';
import { getFeatureByKey } from '@/selectors/featuresConfig';
import { CAPS, FEATURES } from '@/constants';

const { ONBOARDING_STATUSES_V2 } = MOET_CONSTANTS;

const tPrefix = 'products-fe.pages.whats_next_page';

const QUICK_POLLING_TIME = 5 * 1000;
const SLOW_POLLING_TIME = 30 * 1000;

const SUMMARY_STATUS_MAP = {
  [ONBOARDING_STATUSES_V2.TRANSACTION_DETAILS_STARTED]: { type: 'default', icon: IconClock },
  [ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_MANUAL]: { type: 'default', icon: IconClock },
  [ONBOARDING_STATUSES_V2.PAYOUTS_REVIEW_MANUAL]: { type: 'default', icon: IconClock },

  [ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED]: { type: 'default', icon: LoaderIcon },
  [ONBOARDING_STATUSES_V2.PAYOUTS_REVIEW_AUTOMATED]: { type: 'default', icon: LoaderIcon },

  [ONBOARDING_STATUSES_V2.PAYOUTS_REVIEW_AWAITING_FIRST_TRANSACTION]: {
    type: 'default',
    icon: IconClock,
  },

  [ONBOARDING_STATUSES_V2.TRANSACTIONS_ENABLED]: { type: 'default', icon: IconDone },

  [ONBOARDING_STATUSES_V2.TRANSACTIONS_DISABLED]: { type: 'error', icon: IconError },
  [ONBOARDING_STATUSES_V2.SUSPENDED]: { type: 'error', icon: IconError },
};

export const SummaryStatus = ({
  isAwaitingPayoutsRequestAfterFirstTransaction,
  isManagedMerchant = false,
  isShowNoticeOn,
  merchantStatus,
  t,
}) => {
  const content = isAwaitingPayoutsRequestAfterFirstTransaction
    ? t(`${tPrefix}.communication.PAYOUTS_REVIEW_MANUAL`)
    : t(`${tPrefix}.communication.${merchantStatus}`);
  let summary = null;
  let showNotice = false;

  if (isAwaitingPayoutsRequestAfterFirstTransaction) {
    summary = { type: 'default', icon: IconAgent };
  } else {
    summary = SUMMARY_STATUS_MAP[merchantStatus];
    showNotice =
      isShowNoticeOn &&
      !isManagedMerchant &&
      [
        ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_MANUAL,
        ONBOARDING_STATUSES_V2.PAYOUTS_REVIEW_MANUAL,
      ].includes(merchantStatus);
  }

  return (
    <>
      {summary && <Alert type={summary.type} icon={summary.icon} content={content} />}
      {showNotice && (
        <>
          <SpacerVertical extraSmall />
          <Alert type="warning" content={t(`${tPrefix}.communication.notice`)} />
        </>
      )}
    </>
  );
};

SummaryStatus.propTypes = {
  isAwaitingPayoutsRequestAfterFirstTransaction: PropTypes.bool.isRequired,
  merchantStatus: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  isManagedMerchant: PropTypes.bool,
  isShowNoticeOn: PropTypes.bool,
};

export default compose(
  connect(state => ({
    isAwaitingPayoutsRequestAfterFirstTransaction: isAwaitingPayoutsRequestAfterFirstTransaction(
      state,
    ),
    isFetchingMerchant: isFetchingMerchant(state),
    isManagedMerchant: isManagedMerchant(state),
    merchantRegion: merchantRegion(state),
    merchantStatus: getMerchantStatus(state),
    mid: getUserMerchantId(state),
    t: translator(state),
    isShowNoticeOn: getFeatureByKey(FEATURES.SHOW_PRODUCTS_SUMMARY_NOTICE)(state),
  })),
  clientComponents.withCapabilities({ getMerchant: CAPS.V2_MERCHANTS_GET }),
  clientComponents.withPromiseBasedCapabilities({
    getFirstTransaction: CAPS.V2_MERCHANTS_FIRST_TRANSACTION_GET,
  }),
  withLifecycle({
    async onDidMount({ getFirstTransaction, mid, merchantRegion }) {
      await getFirstTransaction({ params: { mid }, region: merchantRegion });
    },
  }),
  withPolling(
    ({ getMerchant, mid, merchantRegion }) => {
      getMerchant({ needsProductRefresh: true, params: { mid }, region: merchantRegion });
    },
    ({ isFetchingMerchant: wasFetchingMerchant }, { isFetchingMerchant }) =>
      wasFetchingMerchant && !isFetchingMerchant,
    {
      intervalCriteria: (prevProps, { merchantStatus }) =>
        [
          ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED,
          ONBOARDING_STATUSES_V2.PAYOUTS_REVIEW_AUTOMATED,
        ].includes(merchantStatus)
          ? QUICK_POLLING_TIME
          : SLOW_POLLING_TIME,
      startCriteria: (prevProps, { merchantStatus }) =>
        ![
          ONBOARDING_STATUSES_V2.NOT_STARTED,
          ONBOARDING_STATUSES_V2.TRANSACTION_DETAILS_STARTED,
          ONBOARDING_STATUSES_V2.COMPLETED,
        ].includes(merchantStatus),
      exitCriteria: (prevProps, { merchantStatus }) =>
        [ONBOARDING_STATUSES_V2.COMPLETED].includes(merchantStatus),
      errorCount: ({ needsMerchantRefreshCount }) => needsMerchantRefreshCount,
      maxDelayTime: SLOW_POLLING_TIME,
    },
  ),
)(SummaryStatus);
